import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
type RuleTraceRowProps = {
  /** Group/branch name shown before the conditions (e.g. "Risk Level"). */
  name?: string;
  /** Whether any rule in this group fired — drives the purple highlight. */
  triggered: boolean;
  /** Triggered condition expressions, in order. Empty when nothing triggered. */
  conditions: string[];
  /** Label shown when nothing triggered (e.g. `Risk Level — no rules triggered`). */
  noTriggerLabel: string;
  /** Content rendered after the "then" connector (the assigned value / branch outcome). */
  outcome: ReactNode;
  /** First entry in the trace — the rail starts at this row's dot. */
  isFirstEntry: boolean;
  /** Last entry in the trace — the rail ends at this row's dot. */
  isLastEntry: boolean;
};
const RAIL = 'w-px flex-1 bg-[var(--border-color-tertiary)]';

/**
 * A single rule/conditional evaluation row in the case review Rules tab.
 */
const RuleTraceRow = t0 => {
  const $ = _c(48);
  const {
    name,
    triggered,
    conditions,
    noTriggerLabel,
    outcome,
    isFirstEntry,
    isLastEntry
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "onboardings.onboardingTrace"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const t2 = isFirstEntry && "invisible";
  let t3;
  if ($[1] !== t2) {
    t3 = cx(RAIL, t2);
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t3) {
    t4 = <span className={t3} />;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = triggered ? "bg-accent" : "bg-gray-300";
  let t6;
  if ($[5] !== t5) {
    t6 = cx("size-1.5 shrink-0 rounded-full", t5);
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t6) {
    t7 = <span className={t6} />;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = isLastEntry && "invisible";
  let t9;
  if ($[9] !== t8) {
    t9 = cx(RAIL, t8);
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== t9) {
    t10 = <span className={t9} />;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10 || $[14] !== t4 || $[15] !== t7) {
    t11 = <div className="flex w-3 shrink-0 flex-col items-center">{t4}{t7}{t10}</div>;
    $[13] = t10;
    $[14] = t4;
    $[15] = t7;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = triggered ? "bg-active border-secondary-hover text-secondary" : "bg-secondary border-primary text-quaternary";
  let t13;
  if ($[17] !== t12) {
    t13 = cx("inline-flex flex-wrap items-center gap-x-3 gap-y-2 px-2 py-1 border rounded-sm text-snippet-1 wrap-anywhere md:wrap-normal", t12);
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== conditions || $[20] !== name || $[21] !== noTriggerLabel || $[22] !== t) {
    t14 = conditions.length > 0 ? <>{name && <span className="text-secondary">{name}:</span>}{conditions.map((expression, index) => <Condition key={expression} connector={index === 0 ? t(_temp) : t(_temp2)} expression={expression} />)}</> : <span>{noTriggerLabel}</span>;
    $[19] = conditions;
    $[20] = name;
    $[21] = noTriggerLabel;
    $[22] = t;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t13 || $[25] !== t14) {
    t15 = <div className={t13}>{t14}</div>;
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t11 || $[28] !== t15) {
    t16 = <div className="flex gap-x-2">{t11}{t15}</div>;
    $[27] = t11;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const t17 = isLastEntry && "invisible";
  let t18;
  if ($[30] !== t17) {
    t18 = cx(RAIL, t17);
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== t18) {
    t19 = <div className="flex w-3 shrink-0 flex-col items-center"><span className={t18} /></div>;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  const t20 = triggered ? "text-secondary" : "text-quaternary";
  let t21;
  if ($[34] !== t20) {
    t21 = cx("pt-2 pb-4 pl-4 text-snippet-1 before:mr-1 before:text-gray-300 before:content-['\u21B3']", t20);
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== t) {
    t22 = t(_temp3);
    $[36] = t;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== outcome || $[39] !== t21 || $[40] !== t22) {
    t23 = <div className={t21}>{t22} {outcome}</div>;
    $[38] = outcome;
    $[39] = t21;
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== t19 || $[43] !== t23) {
    t24 = <div className="flex gap-x-2">{t19}{t23}</div>;
    $[42] = t19;
    $[43] = t23;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== t16 || $[46] !== t24) {
    t25 = <div className="flex flex-col">{t16}{t24}</div>;
    $[45] = t16;
    $[46] = t24;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  return t25;
};
const Condition = t0 => {
  const $ = _c(7);
  const {
    connector,
    expression
  } = t0;
  let t1;
  if ($[0] !== connector) {
    t1 = <span className="text-tertiary">{connector}</span>;
    $[0] = connector;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== expression) {
    t2 = <span className="text-primary font-medium">{expression}</span>;
    $[2] = expression;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
export default RuleTraceRow;
function _temp($) {
  return $.connectors.if;
}
function _temp2($_0) {
  return $_0.connectors.or;
}
function _temp3($_1) {
  return $_1.connectors.then;
}